import React, { useState, useEffect } from 'react';
import { V1_V2X_TRANSACTION_COMPLETION } from '@americanexpress/gravity-core';
import { gatherCapabilities } from '../utils/webAuthn';
import ErrorPage3DS from './ErrorPage3DS';
import PaRes from './PaRes';
import CRes from './CRes';

/* istanbul ignore file */
const GravityRequestHandler = ({
  children,
  pathname,
  gravityCheckoutUrl,
  gravityAuthnUrl,
  queryParams,
  router,
  logger,
  incomingReq,
  completion,
  updateDataHeader,
  dataHeader,
  completionData,
  updateQueryParams,
}) => {
  const [errorPage, setErrorPage] = useState(null);
  const [response, setResponse] = useState(null);

  const validatePaReq = ({ retries, delays }) => {
    const PAREQ_VALIDATION_API = '/payments/digital/gravity/v1/3ds/v1x/processing';
    const { PaReq, TermUrl, MD } = incomingReq.body;
    const apiUrl = `${gravityCheckoutUrl}${PAREQ_VALIDATION_API}`;
    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), 10000);
    const { signal } = abortController;
    const logNames = (() => {
      const names = {
        post: 'PAREQ_VALIDATION_POST',
        success: 'PAREQ_VALIDATION_SUCCESS',
        failure: 'PAREQ_VALIDATION_FAILURE',
      };
      const retryCount = delays.length - retries;
      if (retryCount > 0) {
        names.post = `PAREQ_VALIDATION_REATTEMPT${retryCount > 1 ? `_${retryCount}` : ''}`;
        names.success = `PAREQ_VALIDATION_REATTEMPT${retryCount > 1 ? `_${retryCount}` : ''}_SUCCESS`;
        names.failure = `PAREQ_VALIDATION_REATTEMPT${retryCount > 1 ? `_${retryCount}` : ''}_FAILURE`;
      }
      return names;
    })();

    logger({
      apiUrl,
      name: logNames.post,
      acsTransId: incomingReq.acsTransId,
      requestBody: {
        paReq: PaReq,
        termUrl: TermUrl,
        md: MD,
      },
    });

    fetch(apiUrl, {
      signal,
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({
        paReq: PaReq,
        termUrl: TermUrl,
        md: MD,
      }),
    })
      .then((res) => {
        const data = res.headers.get('data');
        if (data) updateDataHeader(data);
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((resp) => {
        logger({
          apiUrl,
          name: logNames.success,
          acsTransId: incomingReq.acsTransId,
          responseBody: resp,
        });

        if (!resp.errorInd) {
          // ensure payAuth is called at minimum of 1s after inauth load
          const inauthScript = document.querySelector('#inauth');
          if (inauthScript) {
            const inauthTimestamp = new URLSearchParams(inauthScript.src).get('ts');
            const now = new Date().getTime();
            const diff = now - inauthTimestamp;
            const remaining = 1000 - diff;
            const delay = remaining > 0 ? remaining : 0;
            setTimeout(() => {
              logger({
                name: 'INAUTH_BEFORE_REDIRECT',
                acsTransId: resp.acsTransId,
                responseBody: resp,
              });
              updateQueryParams(resp);
              router.replace('/');
            }, delay);
          } else {
            logger({
              name: 'WITHOUT_INAUTH_BEFORE_REDIRECT',
              acsTransId: resp.acsTransId,
              responseBody: resp,
            });
            updateQueryParams(resp);
            router.replace('/');
          }
        } else if (resp.url) {
          setResponse(resp);
        } else {
          throw new Error('Url not found on the response body.');
        }
      })
      .catch((error) => {
        logger(
          {
            apiUrl,
            name: logNames.failure,
            acsTransId: incomingReq.acsTransId,
            reason: error.name === 'AbortError' ? 'The request timed out after 10000ms' : 'Other',
          },
          error
        );
        clearTimeout(timer);
        if (retries > 0) {
          const delay = delays[delays.length - retries];
          setTimeout(() => {
            validatePaReq({ retries: retries - 1, delays });
          }, delay);
        } else {
          setTimeout(() => setErrorPage(true), 300);
        }
      });
  };

  const validateCReq = (retries) => {
    const logNames = (() => {
      const names = {
        post: 'CREQ_VALIDATION_POST',
        success: 'CREQ_VALIDATION_SUCCESS',
        failure: 'CREQ_VALIDATION_FAILURE',
      };

      if (retries > 1) {
        names.post = `CREQ_VALIDATION_REATTEMPT${`_${retries - 1}`}`;
        names.success = `CREQ_VALIDATION_REATTEMPT${`_${retries - 1}`}_SUCCESS`;
        names.failure = `CREQ_VALIDATION_REATTEMPT${`_${retries - 1}`}_FAILURE`;
      }
      return names;
    })();
    const CREQ_VALIDATION_API = '/payments/digital/gravity/v1/3ds/v2x/challenge/browser';
    const { creq, threeDSSessionData } = incomingReq.body;
    const contentType = incomingReq.contentType || 'application/x-www-form-urlencoded';
    const apiUrl = `${gravityCheckoutUrl}${CREQ_VALIDATION_API}`;
    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), 20000);
    const { signal } = abortController;

    const capabilitiesPromise = gatherCapabilities()
      .then((c) => c)
      .catch(() => null);

    capabilitiesPromise.then((clientCapabilities) => {
      const requestBody = {
        cReq: creq,
        threeDSSessionData,
        contentType,
        clientCapabilities,
      };
      logger({
        apiUrl,
        name: logNames.post,
        acsTransId: incomingReq.acsTransId,
        requestBody,
      });
      return fetch(apiUrl, {
        signal,
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
    })
      .then((res) => {
        const data = res.headers.get('data');
        if (data) updateDataHeader(data);
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((resp) => {
        logger({
          apiUrl,
          name: logNames.success,
          acsTransId: incomingReq.acsTransId,
          responseBody: resp,
        });

        if (!resp.errorInd) {
          updateQueryParams(resp);
          if (!resp.scamContentCheck) {
            router.replace('/');
          }
        } else if (resp.url) {
          setResponse(resp);
        } else {
          throw new Error('Url not found on the response body.');
        }
      })
      .catch((error) => {
        logger(
          {
            apiUrl,
            name: logNames.failure,
            acsTransId: incomingReq.acsTransId,
            reason: error.name === 'AbortError' ? 'The request timed out after 10000ms' : 'Other',
          },
          error
        );
        clearTimeout(timer);
        if (retries < 6) {
          const delay = retries === 1 ? 200 : 500;
          setTimeout(() => {
            validateCReq(retries + 1);
          }, delay);
        } else {
          setTimeout(() => setErrorPage(true), 300);
        }
      });
  };

  /* eslint complexity: ["error", 12] */
  const validateRequest = () => {
    if (pathname === '/3ds/v1') {
      if (incomingReq) {
        const { body, acsTransId } = incomingReq;
        const mismatch = body && (body.creq || body.CReq || body.cReq);
        logger({
          name: `${mismatch ? 'PAREQ_RECEIVED_MISMATCH' : 'PAREQ_RECEIVED'}`,
          acsTransId,
          requestBody: body,
        });
      }

      validatePaReq({ retries: 2, delays: [200, 500] });
    } else if (pathname === '/3ds/v2') {
      if (incomingReq) {
        const { body, acsTransId } = incomingReq;
        const mismatch = body && (body.paReq || body.PaReq);
        logger({
          name: `${mismatch ? 'CREQ_RECEIVED_MISMATCH' : 'CREQ_RECEIVED'}`,
          acsTransId,
          requestBody: body,
        });
      }

      validateCReq(1);
    } else {
      // TODO: log case
      setErrorPage(true);
    }
  };

  const postCompletion = (retries = 1) => {
    const apiUrl = `${gravityAuthnUrl}${V1_V2X_TRANSACTION_COMPLETION}`;
    const abortController = new AbortController();
    const timer = setTimeout(() => abortController.abort(), 10000);
    const headers = {
      'Content-Type': 'application/json',
      'acs-trans-id': queryParams.acsTransId,
      Authorization: queryParams.token,
    };
    if (dataHeader) headers.data = dataHeader;
    const { signal } = abortController;
    fetch(apiUrl, {
      signal,
      headers,
      method: 'POST',
      body: JSON.stringify(completionData || completion),
    })
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((resp) => {
        logger({
          apiUrl,
          name: 'TXN_COMPLETION_SUCCESS',
          acsTransId: queryParams.acsTransId,
          responseBody: resp,
        });
        if (resp.url) {
          setResponse(resp);
        } else {
          throw new Error('Url not found on the response body.');
        }
      })
      .catch((error) => {
        const otherData = {
          apiUrl,
          name: retries === 1 ? 'TXN_COMPLETION_FAILED' : `TXN_COMPLETION_REATTEMPT_${retries}_FAILED'`,
          acsTransId: queryParams.acsTransId,
        };

        if (error.name === 'AbortError') otherData.reason = 'The request timed out after 10000ms.';
        logger(otherData, error);

        clearTimeout(timer);
        if (retries < 6 && error.name === 'AbortError') {
          setTimeout(() => {
            postCompletion(retries + 1);
          }, 300);
        } else {
          setErrorPage(true);
        }
      })
      .finally(() => {
        clearTimeout(timer);
      });
  };

  useEffect(() => {
    // TODO: when redirect support no longer needed, show error page if no incoming req
    if (incomingReq) validateRequest();
  }, []);

  useEffect(() => {
    if (completion || completionData) postCompletion(1);
  }, [completion, completionData]);

  if (response) {
    if (incomingReq && incomingReq.type === 'PaReq') {
      logger({
        name: 'PARES_SENT',
        acsTransId: queryParams.acsTransId,
        response: {
          url: response.url,
          PaRes: response.response,
          md: response.md,
        },
      });
      return <PaRes {...response} />;
    }
    if (incomingReq && incomingReq.type === 'CReq') {
      logger({
        name: 'CRES_SENT',
        acsTransId: queryParams.acsTransId,
        response: {
          url: response.url,
          cres: response.response,
          threeDSSessionData: response.threeDSSessionData,
        },
      });
      return <CRes {...response} />;
    }
  }

  if (errorPage) return <ErrorPage3DS />;
  return children;
};

export default GravityRequestHandler;
